"use client"

import React, { Fragment, useState } from "react"

import {
  Divider,
  Paper,
  PaperProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { requestPerPage } from "@tc/ui-dfe/utils/constants"
import { ContentTableColumn, DataModel } from "@tc/ui-dfe/utils/types"
import { Asc, Desc, SortDetail, Sorter, SortOrder } from "../Button"
import { SortableHeader, TablePaginationActions } from "../ContentTable"
import { Pagination } from "../Pagination"
import { DesktopRow, MobileRow } from "./TableRow"

export interface ContentTablePros {
  data: DataModel[]
  totalNumber?: number
  pageCount?: number
  currentPage?: number
  onPageChange?: (
    event: React.ChangeEvent<unknown> | null,
    page: number,
  ) => void
  onSort?: (attribute: string, direction: SortOrder) => void
  columns: ContentTableColumn[]
  onlyOnePage?: boolean
  Filter?: React.ReactNode // TODO will implement this part in future
  variant?: PaperProps["variant"]
  elevation?: PaperProps["elevation"]
  exportData?: React.ReactNode
  sx?: React.ComponentProps<typeof Table>["sx"]
  tableContainerSx?: React.ComponentProps<typeof Table>["sx"]
  scrollRef?: (ref: HTMLElement | null) => void
}

export const ContentTable = ({
  data,
  Filter,
  totalNumber,
  pageCount,
  currentPage,
  onPageChange,
  onSort,
  columns,
  onlyOnePage = false,
  variant = "outlined",
  elevation,
  exportData,
  sx,
  tableContainerSx,
  scrollRef,
}: ContentTablePros) => {
  const theme = useTheme()
  const mdUp = useMediaQuery(theme.breakpoints.up("md")) // middle screen
  const smUp = useMediaQuery(theme.breakpoints.up("sm")) // small screen
  const [resetAttribute, setResetAttribute] = useState("")

  const sortColumn = (column: string, order: SortOrder | undefined) => {
    onSort && onSort(column, order || Desc)
    setResetAttribute(column)
  }

  let defaultSort = 0

  const mobileSortDetail: SortDetail[] = columns
    .filter((col) => col.sort)
    .reduce((accumulator: SortDetail[], col, currentIndex: number) => {
      if (col.sort?.default) {
        defaultSort = currentIndex * 2
      }
      accumulator.push({
        id: col.sort?.sortAttribute || col.valueProp,
        order: Desc,
        label: col.sort?.descLabel || "",
      })
      if (col.sort?.default && col.sort?.defaultSort === Asc) {
        defaultSort += 1
      }
      accumulator.push({
        id: col.sort?.sortAttribute || col.valueProp,
        order: Asc,
        label: col.sort?.ascLabel || "",
      })
      return accumulator
    }, [])
  return (
    <Stack spacing={4} data-testid="content-table-testid" sx={tableContainerSx}>
      <TableContainer
        ref={scrollRef}
        component={Paper}
        variant={variant}
        elevation={elevation}
        square={!mdUp}
        sx={{ borderBottom: "none" }}
        data-testid="test-content-table"
      >
        {!mdUp && (
          <Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{
                px: {
                  xs: 2,
                  sm: 3,
                },
                py: 1.5,
              }}
            >
              {!onlyOnePage && exportData}
              {!onlyOnePage && Filter}
              {!onlyOnePage && (
                <Sorter
                  label="Sort"
                  sortDetails={mobileSortDetail}
                  defaultSortIndex={defaultSort}
                  onSort={(index) => {
                    const selectSort = mobileSortDetail[index]
                    sortColumn(selectSort.id, selectSort.order)
                  }}
                  dividerIndexes={!onlyOnePage ? [1] : undefined}
                />
              )}
            </Stack>
            {!onlyOnePage && <Divider flexItem />}
          </Stack>
        )}
        <Table sx={sx}>
          {mdUp && (
            <TableHead>
              <TableRow>
                {columns.map((col: ContentTableColumn) => {
                  if (col.sort) {
                    return (
                      <TableCell
                        key={col.title}
                        scope="col"
                        sx={col.style || {}}
                      >
                        <SortableHeader
                          title={col.title}
                          sortAttribute={
                            col.sort.sortAttribute || col.valueProp
                          }
                          defaultDirection={col.sort.defaultSort}
                          resetAttribute={resetAttribute}
                          onSort={sortColumn}
                        />
                      </TableCell>
                    )
                  } else if (col.customHeader) return col.customHeader()
                  return (
                    <TableCell key={col.title} scope="col" sx={col.style || {}}>
                      {col.title}
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {data?.map((item, index) => {
              return (
                <Fragment key={`${item.id} - ${index}`}>
                  {mdUp ? (
                    <DesktopRow
                      data={item}
                      columns={columns}
                      rowIndex={index}
                    />
                  ) : (
                    <MobileRow data={item} columns={columns} rowIndex={index} />
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {!onlyOnePage &&
        (smUp ? (
          <Stack spacing={2} justifyContent="center" py={2} px={2}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(event, pageIndex: number) =>
                onPageChange && onPageChange(event, pageIndex)
              }
            />
          </Stack>
        ) : (
          <TablePagination
            component="div"
            count={totalNumber || 0}
            page={(currentPage || 1) - 1}
            onPageChange={(event, pageIndex: number) =>
              onPageChange && onPageChange(event, pageIndex)
            }
            rowsPerPage={requestPerPage}
            labelRowsPerPage=""
            rowsPerPageOptions={[]}
            ActionsComponent={TablePaginationActions}
            sx={{
              display: "flex",
              justifyContent: "center",
              "& .MuiTablePagination-spacer": {
                flex: 0,
              },
            }}
          />
        ))}
    </Stack>
  )
}
